import React from "react";
import { CreditCard, Landmark, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusPill } from "./PayoutKycCard";

const META = {
  pan: { label: "PAN", icon: CreditCard, hint: "Enter your PAN (checked with NSDL), or fetch the Income Tax–issued copy from DigiLocker" },
  bank_account: { label: "Bank account", icon: Landmark, hint: "Account number, IFSC and holder name exactly as on your PAN — where payouts are sent" },
};

// PAN / Bank account rows in the Required documents list. Manual path = existing
// financial change request (OTP → Admin KYC Changes). PAN may also come via DigiLocker.
export function FinancialDocRow({ field, current, pendingRequest, digilockerDoc, busy, digilockerSupported, onRequest, onCancel, onDigiLocker }) {
  const { label, icon: Icon, hint } = META[field];
  const verified = current?.status === "verified_by_admin";
  const dl = digilockerDoc && digilockerDoc.source === "digilocker" ? digilockerDoc : null;
  const dlPending = dl && dl.status === "pending_review";
  const dlRejected = dl && dl.status === "rejected";
  const dlApproved = verified && dl && dl.status === "approved";
  const showProvenance = dlPending || dlApproved;
  const fetching = busy === `digilocker-${field}`;
  const testKey = field === "bank_account" ? "bank" : field;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid={`kyc-doc-${testKey}`}>
      <div className="h-10 w-10 rounded-xl bg-[#FBF4DF] text-[#0A2540] grid place-items-center shrink-0"><Icon size={18} /></div>
      <div className="flex-1 min-w-[220px]">
        <p className="font-bold text-[#0A2540]">{label}</p>
        <p className="text-xs text-slate-500">{hint}</p>
        {current?.masked && (
          <p className="mt-1 text-xs text-slate-700" data-testid={`kyc-${testKey}-value`}>On record: <strong>{current.masked}</strong></p>
        )}
        {pendingRequest && (
          <p className="mt-1 text-[11px] text-slate-500" data-testid={`kyc-${testKey}-pending`}>
            <Clock size={10} className="inline mr-1" /> Requested <strong>{pendingRequest.new_value_masked}</strong>{" "}
            <StatusPill status={pendingRequest.status} />
            {pendingRequest.attachment_ref && <span className="ml-1 text-slate-400">· supporting document attached</span>}
          </p>
        )}
        {showProvenance && (
          <p className="mt-1 text-xs text-slate-600 inline-flex items-center gap-1" data-testid={`kyc-digilocker-provenance-${testKey}`}>
            <Landmark size={11} className="text-[#0A2540]" /> Fetched from DigiLocker{dl.digilocker?.fetched_at ? ` on ${dl.digilocker.fetched_at.slice(0, 10)}` : ""}
            {dl.digilocker?.issuer ? ` · ${dl.digilocker.issuer}` : ""}
          </p>
        )}
        {dlRejected && dl.review_note && <p className="mt-1 text-xs text-rose-600">Reviewer note: {dl.review_note}</p>}
        {!pendingRequest && current?.status === "rejected" && current?.rejection_reason && (
          <p className="mt-1 text-xs text-rose-600" data-testid={`kyc-${testKey}-rejection-reason`}>Reviewer note: {current.rejection_reason}</p>
        )}
      </div>
      {showProvenance && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#0A2540] text-[#D4AF37]" data-testid={`kyc-source-${testKey}`}>
          <Landmark size={10} /> DigiLocker
        </span>
      )}
      {verified ? (
        <>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800" data-testid={`kyc-status-${testKey}`}>Verified</span>
          <span className="px-3 py-2 rounded-xl text-xs font-bold bg-slate-100 text-slate-500 inline-flex items-center gap-1" data-testid={`kyc-done-${testKey}`}><CheckCircle2 size={12} /> {dlApproved ? "Fetched & approved" : "Verified by Admin"}</span>
        </>
      ) : dlPending ? (
        <>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800" data-testid={`kyc-status-${testKey}`}>Under review</span>
          <span className="px-3 py-2 rounded-xl text-xs font-bold bg-[#FBF4DF] text-[#0A2540] inline-flex items-center gap-1" data-testid={`kyc-fetched-${testKey}`}><CheckCircle2 size={12} /> Fetched — nothing else needed</span>
        </>
      ) : pendingRequest ? (
        <>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800" data-testid={`kyc-status-${testKey}`}>
            {pendingRequest.status === "pending_otp" ? "Awaiting OTP" : "Under review"}
          </span>
          {pendingRequest.status === "pending_otp" ? (
            <Button size="sm" variant="outline" className="rounded-xl text-xs h-9" onClick={() => onCancel(pendingRequest)} data-testid={`kyc-${testKey}-cancel`}>
              <XCircle size={12} className="mr-1" /> Cancel
            </Button>
          ) : (
            <span className="px-3 py-2 rounded-xl text-xs font-bold bg-[#FBF4DF] text-[#0A2540] inline-flex items-center gap-1" data-testid={`kyc-submitted-${testKey}`}>
              <CheckCircle2 size={12} /> Submitted — awaiting Admin
            </span>
          )}
        </>
      ) : (
        <div className="flex flex-wrap gap-2">
          {current?.status === "rejected" && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800" data-testid={`kyc-status-${testKey}`}>Rejected — resubmit</span>
          )}
          <Button size="sm" onClick={() => onRequest(field)} disabled={fetching} data-testid={`kyc-request-${testKey}`}
            className={`rounded-xl text-xs h-9 font-bold ${digilockerSupported ? "bg-white border border-slate-200 text-[#0A2540] hover:border-[#0A2540]" : "adv-btn-primary"}`} variant={digilockerSupported ? "outline" : "default"}>
            {field === "pan" ? "Enter PAN" : "Add bank account"}
          </Button>
          {digilockerSupported && (
            <button type="button" onClick={() => onDigiLocker(field)} disabled={fetching} data-testid={`kyc-digilocker-${testKey}`}
              className="px-3 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1 bg-[#0A2540] text-[#D4AF37] hover:opacity-90 disabled:opacity-60">
              {fetching ? <Loader2 size={14} className="animate-spin" /> : <Landmark size={12} />} Fetch from DigiLocker
            </button>
          )}
        </div>
      )}
    </div>
  );
}
